import React, { useState, useEffect, useRef, Suspense, lazy } from 'react';
import { Routes, Route, Link, useNavigate, useLocation } from 'react-router-dom';
import { Menu, X, ArrowUpRight } from 'lucide-react';

const smoothScrollToId = (id: string) => {
  const el = document.getElementById(id);
  if (!el) return;
  const top = el.getBoundingClientRect().top + window.scrollY - 88;
  window.scrollTo({ top, behavior: 'smooth' });
};

// --- Pages ---
const ServicePage = lazy(() => import('./pages/ServicePage'));
const BlogPage = lazy(() => import('./pages/BlogPage'));
const BlogPostPage = lazy(() => import('./pages/BlogPostPage'));
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = lazy(() => import('./pages/TermsOfService'));

// --- Home sections ---
import Services from './components/home/Services';
import Process from './components/home/Process';
import WhyEmerge from './components/home/WhyEmerge';
import Booking from './components/home/Booking';
import LatestInsights from './components/home/LatestInsights';
import LeadMagnet from './components/home/LeadMagnet';
import FAQ from './components/home/FAQ';
import ContactForm from './components/home/ContactForm';

import SEO from './components/SEO';
import Footer from './components/Footer';
import { ScrollProgress, RevealSection } from './components/Animations';
import Logo from './components/Logo';
import IntegrationsMarquee from './components/IntegrationsMarquee';

// ---------------------------------------------------------------------------
// ScrollToTop — resets scroll on route change (ignores hash-only changes).
// ---------------------------------------------------------------------------
const ScrollToTop = () => {
  const { pathname, hash } = useLocation();
  const isFirstLoad = useRef(true);

  useEffect(() => {
    // On initial page load / refresh: always scroll to top and clear any stale hash.
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      if (hash) {
        window.history.replaceState(null, '', pathname + window.location.search);
      }
      window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
      return;
    }

    // Subsequent SPA navigations: if there's a hash, scroll to that section.
    if (hash) {
      const id = hash.slice(1);
      const timers = [80, 260, 600].map((ms) =>
        window.setTimeout(() => {
          const el = document.getElementById(id);
          if (!el) return;
          const top = el.getBoundingClientRect().top + window.scrollY - 88;
          window.scrollTo({ top, behavior: 'smooth' });
        }, ms)
      );
      return () => timers.forEach((t) => window.clearTimeout(t));
    }

    // SPA navigation with no hash: reset to top.
    window.scrollTo({ top: 0, left: 0, behavior: 'instant' as ScrollBehavior });
  }, [pathname, hash]);

  return null;
};

// ---------------------------------------------------------------------------
// Background stage — base + grain + ambient halo. No animated orbs.
// ---------------------------------------------------------------------------
const Stage = () => (
  <div className="stage" aria-hidden="true">
    <div className="stage__halo" />
    <div className="stage__halo stage__halo--cool" />
    <div className="stage__grain" />
  </div>
);

// ---------------------------------------------------------------------------
// Navbar
// ---------------------------------------------------------------------------
const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();

  const handleNavClick = (
    e: React.MouseEvent<HTMLAnchorElement>,
    href: string
  ) => {
    if (!href.startsWith('/#')) return;
    const id = href.slice(2);
    e.preventDefault();
    setIsMobileMenuOpen(false);
    if (location.pathname === '/') {
      smoothScrollToId(id);
      window.history.pushState(null, '', `#${id}`);
    } else {
      navigate(`/#${id}`);
    }
  };

  useEffect(() => {
    const handleScroll = () => setIsScrolled(window.scrollY > 12);
    handleScroll();
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setIsMobileMenuOpen(false);
    };
    if (isMobileMenuOpen) window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isMobileMenuOpen]);

  const navLinks = [
    { name: 'Services', href: '/#services' },
    { name: 'Process', href: '/#process' },
    { name: 'Why Us', href: '/#why-us' },
    { name: 'Insights', href: '/blog/' },
    { name: 'FAQ', href: '/#faq' },
  ];

  return (
    <nav
      role="navigation"
      aria-label="Main navigation"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        isScrolled
          ? 'py-3 bg-[color:var(--color-bg-base)]/80 backdrop-blur-xl border-b border-white/5'
          : 'py-5 bg-transparent'
      }`}
    >
      <div className="container-custom flex items-center justify-between gap-6">
        <Link to="/" className="flex items-center gap-3 shrink-0" aria-label="Emerge Automations, Home">
          <Logo className="h-8 md:h-9" />
        </Link>

        {/* Desktop nav */}
        <div className="hidden lg:flex items-center gap-8">
          {navLinks.map((link) => (
            <Link
              key={link.name}
              to={link.href}
              onClick={(e) => handleNavClick(e, link.href)}
              className="text-sm font-medium text-zinc-400 hover:text-white transition-colors"
            >
              {link.name}
            </Link>
          ))}
        </div>

        <div className="hidden lg:flex items-center gap-4">
          <span className="live-pill" aria-label="Automations live 24/7">
            <span className="live-pill__dot" />
            <span>24/7 · Live</span>
          </span>
          <Link
            to="/#calendly-booking"
            onClick={(e) => handleNavClick(e, '/#calendly-booking')}
            className="btn-primary"
          >
            Book audit
            <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
          </Link>
        </div>

        {/* Mobile toggle */}
        <button
          className="lg:hidden w-10 h-10 rounded-[var(--radius-sm)] border border-white/10 text-white flex items-center justify-center"
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          aria-label={isMobileMenuOpen ? 'Close menu' : 'Open menu'}
          aria-expanded={isMobileMenuOpen}
        >
          {isMobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {isMobileMenuOpen && (
        <div
          className="absolute top-full left-0 right-0 lg:hidden border-b border-white/10"
          style={{ background: 'var(--color-bg-base)' }}
        >
          <div className="container-custom py-6 flex flex-col gap-1">
              {navLinks.map((link) => (
                <Link
                  key={link.name}
                  to={link.href}
                  onClick={(e) => {
                    handleNavClick(e, link.href);
                    setIsMobileMenuOpen(false);
                  }}
                  className="flex items-center justify-between py-3 text-base font-medium text-zinc-300 hover:text-white border-b border-white/5"
                >
                  {link.name}
                  <ArrowUpRight className="w-4 h-4 text-zinc-600" strokeWidth={1.5} />
                </Link>
              ))}
              <div className="mt-4 flex items-center justify-between gap-3">
                <span className="live-pill">
                  <span className="live-pill__dot" />
                  <span>Live</span>
                </span>
                <Link
                  to="/#calendly-booking"
                  onClick={(e) => {
                    handleNavClick(e, '/#calendly-booking');
                    setIsMobileMenuOpen(false);
                  }}
                  className="btn-primary flex-1"
                >
                  Book audit
                </Link>
              </div>
            </div>
        </div>
      )}
    </nav>
  );
};

// ---------------------------------------------------------------------------
// Hero — metric-first editorial layout.
// Content copy preserved verbatim from original (SEO-critical).
// ---------------------------------------------------------------------------
const Hero = () => {
  const scrollTo = (id: string) => {
    const el = document.getElementById(id);
    if (!el) return;
    const top = el.getBoundingClientRect().top + window.scrollY - 88;
    window.scrollTo({ top, behavior: 'smooth' });
    window.history.pushState(null, '', `#${id}`);
  };

  const metrics = [
    { label: 'Hours saved', value: '10,000+' },
    { label: 'System uptime', value: '99.9%' },
    { label: 'Time to launch', value: '< 14d' },
  ];

  return (
    <section
      id="hero"
      className="relative pt-32 md:pt-40 pb-20 md:pb-32 overflow-hidden"
    >
      <div className="container-custom relative z-10">
        <RevealSection className="max-w-5xl">
          <div className="flex items-center gap-3 mb-8">
            <span className="live-pill">
              <span className="live-pill__dot" />
              <span>AI automation · UK &amp; USA</span>
            </span>
          </div>

          <h1 className="font-display font-semibold text-white leading-[1.02] tracking-[-0.035em] text-[clamp(2.75rem,7vw,5.5rem)] mb-8">
            Scale your business{' '}
            <br />
            <span className="text-[color:var(--color-accent)]">
              with AI automation
            </span>
          </h1>

          <p className="max-w-3xl text-lg md:text-xl text-zinc-400 leading-[1.6] mb-10">
            We build custom AI voice agents and automated workflows that handle your repetitive tasks,
            saving you hundreds of hours and helping your business grow faster. From intelligent phone answering
            to complex n8n workflow integrations, we're the AI automation agency for SMEs in the UK and USA.
          </p>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-16 md:mb-20">
            <button onClick={() => scrollTo('calendly-booking')} className="btn-primary">
              Book your free AI audit
              <ArrowUpRight className="w-4 h-4" strokeWidth={2} />
            </button>
            <button onClick={() => scrollTo('services')} className="btn-secondary">
              See how it works
            </button>
          </div>
        </RevealSection>

        {/* Metric-first signature row */}
        <RevealSection>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-px bg-white/5 border border-white/10 rounded-[var(--radius-lg)] overflow-hidden">
            {metrics.map((m) => (
              <div
                key={m.label}
                className="px-6 py-6 md:px-8 md:py-8 bg-[color:var(--color-bg-base)] flex flex-col gap-2"
              >
                <span className="eyebrow">{m.label}</span>
                <span
                  data-mono
                  className="text-3xl md:text-4xl font-medium text-white tracking-[-0.02em] tabular-nums"
                  style={{ fontFeatureSettings: '"tnum" on' }}
                >
                  {m.value}
                </span>
              </div>
            ))}
          </div>
        </RevealSection>

      </div>
    </section>
  );
};

// ---------------------------------------------------------------------------
// App shell
// ---------------------------------------------------------------------------
export default function App() {
  const homeSchema = {
    '@context': 'https://schema.org',
    '@type': 'ProfessionalService',
    name: 'Emerge Automations',
    description:
      'Premier AI Automation & Strategy Agency specializing in AI voice agents, n8n workflows, and business process automation for SMEs.',
    url: 'https://emergeautomations.com',
    logo: 'https://emergeautomations.com/logo.png',
    image: 'https://emergeautomations.com/og-image.png',
    telephone: '+44-20-XXXX-XXXX',
    priceRange: '$$',
    address: {
      '@type': 'PostalAddress',
      streetAddress: '71-75 Shelton Street',
      addressLocality: 'London',
      postalCode: 'WC2H 9JQ',
      addressCountry: 'UK',
    },
    geo: {
      '@type': 'GeoCoordinates',
      latitude: 51.5148,
      longitude: -0.1235,
    },
    openingHoursSpecification: {
      '@type': 'OpeningHoursSpecification',
      dayOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
      opens: '09:00',
      closes: '18:00',
    },
    serviceType: ['AI Automation', 'AI Voice Agents', 'Workflow Automation', 'AI Consulting'],
    areaServed: ['UK', 'USA', 'Europe'],
    sameAs: [
      'https://twitter.com/emergeautomations',
      'https://linkedin.com/company/emergeautomations',
    ],
  };

  useEffect(() => {
    const scrollToElement = () => {
      if (!window.location.hash) return;
      const id = window.location.hash.replace('#', '');
      const el = document.getElementById(id);
      if (!el) return;
      const scroll = () => {
        const top = el.getBoundingClientRect().top + window.scrollY - 88;
        window.scrollTo({ top, behavior: 'smooth' });
      };
      setTimeout(scroll, 100);
      setTimeout(scroll, 500);
      setTimeout(scroll, 1000);
    };
    scrollToElement();
    window.addEventListener('hashchange', scrollToElement);
    return () => window.removeEventListener('hashchange', scrollToElement);
  }, []);

  return (
    <div
      suppressHydrationWarning
      className="min-h-[100dvh] flex flex-col relative"
    >
      <ScrollToTop />
      <Stage />
      <ScrollProgress />
      <Navbar />

      <main className="flex-grow">
        <Suspense
          fallback={
            <div className="min-h-[50vh] flex items-center justify-center">
              <div className="w-8 h-8 rounded-full border-2 border-[color:var(--color-accent)] border-t-transparent animate-spin" />
            </div>
          }
        >
          <Routes>
            <Route path="/services/:id" element={<ServicePage />} />
            <Route path="/blog" element={<BlogPage />} />
            <Route path="/blog/:id" element={<BlogPostPage />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route path="/terms-of-service" element={<TermsOfService />} />
            <Route
              path="/"
              element={
                <>
                  <SEO
                    title="Emerge Automations | AI Voice & Workflows"
                    description="We build custom AI voice agents, chatbots, and automated workflows that save businesses hundreds of hours. Expert AI automation consulting for SMEs."
                    keywords="AI automation, AI voice agents, business automation UK, business automation USA, n8n automation, custom AI chatbots, SME AI strategy"
                    canonical="https://emergeautomations.com/"
                    schemaData={homeSchema}
                    faq={[
                      { question: 'How long does implementation take?', answer: 'Most of our core automation systems are deployed within days or weeks, depending on complexity. We focus on rapid impact.' },
                      { question: 'Do we need a technical team?', answer: 'No. We handle everything from design to deployment. We also provide training for your existing staff.' },
                      { question: 'Is our data secure?', answer: 'Absolutely. We prioritize data privacy and can build solutions that run on private servers or within your own cloud environment.' },
                      { question: 'Do you offer ongoing support?', answer: 'Yes, we provide 24/7 monitoring and support packages to ensure your AI systems continue to run smoothly as your business scales.' },
                      { question: 'Can AI integrate with my existing CRM?', answer: 'Absolutely. We specialize in connecting AI with popular CRMs like HubSpot, Salesforce, and Pipedrive, as well as custom-built databases.' },
                    ]}
                  />
                  <Hero />
                  <IntegrationsMarquee />
                  <Services />
                  <Process />
                  <WhyEmerge />
                  <Booking />
                  <LatestInsights />
                  <LeadMagnet />
                  <FAQ />
                  <ContactForm />
                </>
              }
            />
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </Suspense>
      </main>

      <Footer />
    </div>
  );
}
